import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { getGlobalDashboard, getSiteDashboard, getSites, getSustainability, downloadSustainabilityReport, getSavingsDashboard, getMyWantedMatches } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  Package, TrendingDown, AlertTriangle, ArrowUpRight, Building, Clock, DollarSign,
  Leaf, Recycle, TreePine, Factory, FileDown, Loader2, ShieldAlert, Layers, BarChart3, ClipboardList
} from 'lucide-react';
import { fmt2, fmtEUR } from '@/lib/utils';

const AGE_COLORS = {
  '0_90': '#22C55E',
  '91_180': '#84CC16',
  '181_360': '#EAB308',
  'gt_360': '#EF4444'
};

const CAT_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#14B8A6', '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];

export const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isSiteManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sustainability, setSustainability] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [savings, setSavings] = useState(null);
  const [wantedMatchCount, setWantedMatchCount] = useState(null);

  const AGE_LABELS = {
    '0_90': t('dashboard.age0_90'),
    '91_180': t('dashboard.age91_180'),
    '181_360': t('dashboard.age181_360'),
    'gt_360': t('dashboard.ageGt360')
  };

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    loadSites();
    loadSustainability();
    loadSavings();
    getMyWantedMatches().then((r) => setWantedMatchCount(r.data.count)).catch(() => {});
  }, [user]);

  useEffect(() => { loadDashboard(); }, [selectedSite]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = selectedSite === 'all'
        ? await getGlobalDashboard()
        : await getSiteDashboard(selectedSite);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally { setLoading(false); }
  };

  const loadSites = async () => {
    try { setSites((await getSites()).data); } catch (e) { console.error(e); }
  };

  const loadSustainability = async () => {
    try { setSustainability((await getSustainability()).data); } catch (e) { console.error(e); }
  };

  const loadSavings = async () => {
    try { setSavings((await getSavingsDashboard()).data); } catch (e) { console.error(e); }
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const params = { lang: i18n.language };
      if (selectedSite !== 'all') params.site_id = selectedSite;
      const response = await downloadSustainabilityReport(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esg_report_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) { console.error(error); }
    finally { setReportLoading(false); }
  };

  const pieChartData = dashboard?.age_buckets?.map(bucket => ({
    name: AGE_LABELS[bucket._id] || bucket._id,
    value: bucket.total_value || 0,
    count: bucket.count,
    color: AGE_COLORS[bucket._id] || '#94A3B8'
  })) || [];

  const siteRankingData = dashboard?.site_ranking?.slice(0, 10).map(site => ({
    name: site.site_name?.slice(0, 15) || 'Unknown',
    slowMoving: site.slow_moving_count,
    totalValue: site.total_value || 0
  })) || [];

  const categoryData = dashboard?.category_breakdown?.map((cs, i) => ({
    name: cs.category_name?.slice(0, 20) || 'Unknown',
    value: cs.total_value || 0,
    slowMoving: cs.slow_moving_count || 0,
    color: CAT_COLORS[i % CAT_COLORS.length]
  })) || [];

  const totalMaterials = dashboard?.total_skus || dashboard?.age_buckets?.reduce((sum, b) => sum + (b.count || 0), 0) || 0;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-manrope text-3xl font-bold" data-testid="dashboard-title">{t('nav.dashboard')}</h1>
            <p className="text-muted-foreground">{t('dashboard.subtitle')}</p>
            <p className="text-xs text-muted-foreground/70 mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t('dashboard.agingAutoCalculated')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger className="w-[200px]" data-testid="site-filter-select">
                <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder={t('dashboard.siteFilter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="site-filter-all">{t('dashboard.allSites')}</SelectItem>
                {sites.map(site => (
                  <SelectItem key={site.site_id} value={site.site_id} data-testid={`site-filter-${site.site_id}`}>
                    {site.site_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <div className="flex gap-3">
                <Button variant="outline" asChild><Link to="/import">{t('nav.import')}</Link></Button>
                <Button className="bg-orange-500 hover:bg-orange-600" asChild><Link to="/admin">{t('nav.admin')}</Link></Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : (
          <>
            {/* KPI Cards - Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {selectedSite === 'all' ? (
                <>
                  <Card data-testid="kpi-total-materials">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.totalMaterials')}</CardTitle>
                      <Package className="h-4 w-4 text-blue-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-blue-600">{totalMaterials}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.totalMaterialsDesc')}</p>
                    </CardContent>
                  </Card>
                  <Card data-testid="kpi-total-value">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.totalValue')}</CardTitle>
                      <DollarSign className="h-4 w-4 text-emerald-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-emerald-600">
                        {'\u20AC'}{fmtEUR(dashboard?.total_value_kEUR || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.totalValueDesc')}</p>
                    </CardContent>
                  </Card>
                  <Card data-testid="kpi-slow-moving">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.slowMoving')}</CardTitle>
                      <TrendingDown className="h-4 w-4 text-yellow-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-yellow-600">
                        {'\u20AC'}{fmtEUR(dashboard?.slow_moving_total_kEUR || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.slowMovingDesc')}</p>
                    </CardContent>
                  </Card>
                  <Card data-testid="kpi-value-at-risk" className="border-red-200/50">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.valueAtRisk')}</CardTitle>
                      <ShieldAlert className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-red-600">
                        {'\u20AC'}{fmtEUR(dashboard?.non_moving_total_kEUR || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.valueAtRiskDesc')}</p>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <>
                  <Card data-testid="kpi-site-materials">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.totalMaterials')}</CardTitle>
                      <Package className="h-4 w-4 text-blue-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-blue-600">{totalMaterials}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.totalMaterialsDesc')}</p>
                    </CardContent>
                  </Card>
                  <Card data-testid="kpi-site-overdue">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.overdueItems')}</CardTitle>
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-red-600">{dashboard?.overdue_count || 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.overdueDesc')}</p>
                    </CardContent>
                  </Card>
                  <Card data-testid="kpi-site-pending">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.sitePending')}</CardTitle>
                      <Clock className="h-4 w-4 text-orange-500" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-orange-600">{dashboard?.pending_requests_count || 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.sitePendingDesc')}</p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Row 2: KPI mini cards */}
            {selectedSite === 'all' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <ArrowUpRight className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-lg font-bold">{dashboard?.monthly_internal_transfers || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.transfersDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <DollarSign className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-lg font-bold">{dashboard?.monthly_external_sales || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.externalSalesDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="text-lg font-bold">{dashboard?.pending_requests_count || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.pendingRequests')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-lg font-bold">{dashboard?.overdue_count || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.overdueDesc')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Wanted Ads Match Widget */}
            {wantedMatchCount !== null && (
              <div className="mb-6">
                <Link to="/wanted">
                  <Card className="border-purple-200 dark:border-purple-800 hover:shadow-md transition-shadow cursor-pointer" data-testid="wanted-match-widget">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                          <ClipboardList className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-purple-700 dark:text-purple-300">
                            {wantedMatchCount === 0
                              ? 'Keine passenden Gesuche'
                              : `${wantedMatchCount} ${wantedMatchCount === 1 ? 'Gesuch passt' : 'Gesuche passen'} zu Ihren Materialien`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Plattformweite Nachfrage nach Surplus-Materialien
                          </p>
                        </div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-purple-500 shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              </div>
            )}

            {/* Charts Row */}
            <div className={`grid grid-cols-1 ${selectedSite === 'all' ? 'lg:grid-cols-2' : ''} gap-6 mb-8`}>
              <Card data-testid="age-distribution-chart">
                <CardHeader>
                  <CardTitle>{t('dashboard.ageChart')}</CardTitle>
                  <CardDescription>{t('dashboard.ageChartDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    {pieChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieChartData} cx="50%" cy="50%" labelLine={false} outerRadius={100} dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                            {pieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(value) => `\u20AC${fmtEUR(value)}`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">{t('common.noResults')}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
              {selectedSite === 'all' && (
                <Card data-testid="site-ranking-chart">
                  <CardHeader>
                    <CardTitle>{t('dashboard.siteRanking')}</CardTitle>
                    <CardDescription>{t('dashboard.siteRankingDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      {siteRankingData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={siteRankingData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Bar dataKey="slowMoving" fill="#F97316" name={t('dashboard.slowMovingItems')} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">{t('common.noResults')}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Category Breakdown & Top Risk Materials - New sections */}
            {selectedSite === 'all' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Category Breakdown */}
                <Card data-testid="category-breakdown-chart">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-violet-500" />
                      {t('dashboard.categoryBreakdown')}
                    </CardTitle>
                    <CardDescription>{t('dashboard.categoryBreakdownDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categoryData.length > 0 ? (
                      <div className="space-y-3">
                        {categoryData.map((cat, i) => (
                          <div key={i} className="flex items-center gap-3" data-testid={`category-row-${i}`}>
                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium truncate">{cat.name}</span>
                                <span className="text-sm text-muted-foreground ml-2">
                                  {'\u20AC'}{fmtEUR(cat.value || 0)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{
                                    backgroundColor: cat.color,
                                    width: `${Math.min(100, ((cat.value || 0) / Math.max(1, categoryData[0]?.value || 1)) * 100)}%`
                                  }} />
                                </div>
                                {cat.slowMoving > 0 && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 text-yellow-600 border-yellow-300">
                                    {cat.slowMoving} slow
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-muted-foreground">{t('common.noResults')}</div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Risk Materials */}
                <Card data-testid="top-risk-materials">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldAlert className="h-5 w-5 text-red-500" />
                      {t('dashboard.topRisk')}
                    </CardTitle>
                    <CardDescription>{t('dashboard.topRiskDesc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dashboard?.top_risk_materials?.length > 0 ? (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {dashboard.top_risk_materials.map((item, i) => (
                          <Link
                            key={i}
                            to={`/materials/${item.material_id}`}
                            className="flex items-center justify-between p-3 rounded-lg bg-red-50/50 dark:bg-red-900/10 hover:bg-red-100/50 dark:hover:bg-red-900/20 transition-colors"
                            data-testid={`risk-material-${i}`}
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{item.common_title || item.article_number}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.site_name} | {item.quantity} {t('dashboard.items')} | {AGE_LABELS[item.age_bucket]}
                              </p>
                              {item.production_date && (
                                <p className="text-xs text-muted-foreground/70">
                                  Prod: {new Date(item.production_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <Badge variant="destructive" className="shrink-0 ml-2">
                              {'\u20AC'}{fmtEUR(item.book_value_kEUR || 0)}
                            </Badge>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-muted-foreground">{t('common.noResults')}</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Overdue Items List */}
            {dashboard?.overdue_items?.length > 0 && (
              <Card className="mb-8" data-testid="overdue-items-list">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    {t('dashboard.overdueTitle')}
                  </CardTitle>
                  <CardDescription>{t('dashboard.overdueDesc2')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboard.overdue_items.slice(0, 5).map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 rounded-lg" data-testid={`overdue-item-${index}`}>
                        <div>
                          <p className="font-medium">{item.article_number}</p>
                          <p className="text-sm text-muted-foreground">{item.quantity} {item.uom}</p>
                        </div>
                        <Badge variant="destructive">{'\u20AC'}{fmtEUR(item.book_value_kEUR || 0)}</Badge>
                      </div>
                    ))}
                  </div>
                  {dashboard.overdue_count > 5 && (
                    <Button variant="outline" className="w-full mt-4" asChild data-testid="view-all-overdue-btn">
                      <Link to="/search?age=gt_360">{t('dashboard.viewAllOverdue', { count: dashboard.overdue_count })}</Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="quick-actions">
              <Button variant="outline" className="h-auto py-4" asChild data-testid="quick-action-nonmoving">
                <Link to="/search?age=gt_360" className="flex flex-col items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                  <span>{t('dashboard.viewNonMoving')}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4" asChild data-testid="quick-action-requests">
                <Link to="/requests" className="flex flex-col items-center gap-2">
                  <Package className="h-6 w-6 text-blue-500" />
                  <span>{t('dashboard.pendingRequestsLink')}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4" asChild data-testid="quick-action-group">
                <Link to="/my-group" className="flex flex-col items-center gap-2">
                  <Layers className="h-6 w-6 text-violet-500" />
                  <span>{t('nav.myGroup')}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4" asChild data-testid="quick-action-import">
                <Link to="/import" className="flex flex-col items-center gap-2">
                  <Building className="h-6 w-6 text-green-500" />
                  <span>{t('dashboard.importMaterials')}</span>
                </Link>
              </Button>
            </div>

            {/* Savings Dashboard Widget */}
            {savings && (
              <Card className="mb-8 border-emerald-200/50 dark:border-emerald-800/50" data-testid="savings-dashboard-widget">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-emerald-500" />
                    Einsparungen
                  </CardTitle>
                  <CardDescription>
                    Einsparungen basierend auf EK-Preisen aus der Standard-Materialliste
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 p-4 border border-emerald-200 dark:border-emerald-800" data-testid="savings-total">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Gesamteinsparung</p>
                      <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        {savings.total_savings_eur > 0 ? `€${savings.total_savings_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}` : '€0,00'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-blue-50/50 dark:bg-blue-900/10 p-4 border border-blue-200 dark:border-blue-800" data-testid="savings-count">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Transaktionen mit Einsparung</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {savings.transactions_with_savings}
                      </p>
                    </div>
                    <div className="rounded-lg bg-amber-50/50 dark:bg-amber-900/10 p-4 border border-amber-200 dark:border-amber-800" data-testid="savings-not-calculable">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Nicht errechenbar</p>
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {savings.not_calculable_count}
                      </p>
                    </div>
                  </div>
                  {savings.details?.length > 0 && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {savings.details.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800 text-sm" data-testid={`saving-detail-${i}`}>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{d.material_title}</p>
                            <p className="text-xs text-muted-foreground">
                              {d.qty} Stk. | EK: €{d.ek_price_eur}/Stk. | Plattform: €{d.platform_price_per_unit}/Stk.
                            </p>
                          </div>
                          <span className="font-bold text-emerald-600 ml-2 whitespace-nowrap">
                            +€{d.total_saving_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sustainability Impact Section */}
            {sustainability && (
              <div data-testid="dashboard-sustainability">
                <Card className="border-emerald-200/50 dark:border-emerald-800/50 overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Leaf className="h-5 w-5 text-emerald-500" />
                            {t('sustainability.title')}
                          </CardTitle>
                          <CardDescription>{t('sustainability.subtitle')}</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleDownloadReport} disabled={reportLoading}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="download-esg-report-btn">
                          {reportLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                          {reportLoading ? t('sustainability.reportGenerating') : t('sustainability.downloadReport')}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-lg bg-white/60 dark:bg-slate-900/60 p-4 border border-emerald-100 dark:border-emerald-900/30" data-testid="sust-co2-card">
                          <div className="flex items-center gap-2 mb-2">
                            <TreePine className="h-4 w-4 text-emerald-500" />
                            <span className="text-xs font-medium text-muted-foreground">{t('sustainability.co2Saved')}</span>
                          </div>
                          <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                            {sustainability.estimated_co2_saved_kg >= 1000 
                              ? `${(sustainability.estimated_co2_saved_kg / 1000).toFixed(1)} ${t('sustainability.tonUnit')}`
                              : `${sustainability.estimated_co2_saved_kg.toFixed(0)} ${t('sustainability.kgUnit')}`}
                          </p>
                        </div>
                        <div className="rounded-lg bg-white/60 dark:bg-slate-900/60 p-4 border border-emerald-100 dark:border-emerald-900/30" data-testid="sust-redistributed-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Recycle className="h-4 w-4 text-teal-500" />
                            <span className="text-xs font-medium text-muted-foreground">{t('sustainability.materialsRedistributed')}</span>
                          </div>
                          <p className="text-xl font-bold text-teal-600 dark:text-teal-400">{sustainability.materials_redistributed}</p>
                        </div>
                        <div className="rounded-lg bg-white/60 dark:bg-slate-900/60 p-4 border border-emerald-100 dark:border-emerald-900/30" data-testid="sust-circular-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Factory className="h-4 w-4 text-blue-500" />
                            <span className="text-xs font-medium text-muted-foreground">{t('sustainability.circularRate')}</span>
                          </div>
                          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{sustainability.circular_economy_rate}%</p>
                        </div>
                        <div className="rounded-lg bg-white/60 dark:bg-slate-900/60 p-4 border border-emerald-100 dark:border-emerald-900/30" data-testid="sust-at-risk-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Leaf className="h-4 w-4 text-amber-500" />
                            <span className="text-xs font-medium text-muted-foreground">{t('sustainability.atRisk')}</span>
                          </div>
                          <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{sustainability.at_risk_materials}</p>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
